import { AnchorHTMLAttributes, ReactNode } from "react";
import { Box, Divider, List, ListItem, Stack, Typography, useTheme } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { MenuList, MenuListItemVariant, SystmAppIcon, WahooOnly } from "@WahooFitness/redesignr";
import FeatureCarousel from "./FeaturesCarousel";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";

function TrackedSystmLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { logEvent } = useLogEvent();
  return (
    <a
      {...props}
      onClick={(e) => {
        logEvent("systm_link_clicked", {
          event_type: EventType.click,
          click_target: ClickTarget.systm_app,
          source_page_name: SourcePage.my_subscription,
        });
        props.onClick?.(e);
      }}
    />
  );
}

export type SubscriptionData = {
  tag: ReactNode | null;
  items: { label: string; value: string | ReactNode }[];
  description?: ReactNode | null;
  action: ReactNode | null;
};
function SubscriptionPage({ subscriptionData }: { subscriptionData: SubscriptionData }) {
  const { palette } = useTheme();

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        py={2}
        bgcolor={palette.background.level1}
        // sx below is a way to extend the box background to the full width of the screen
        sx={{
          width: "100vw",
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
        }}
      >
        <Box display="flex" justifyContent="center" width="100%">
          <Box maxWidth="sm" width="100%" display="flex" flexDirection="column" gap={2} px={2}>
            <Box bgcolor="secondary.light" borderRadius={1.5} padding={1.5}>
              <Stack
                direction="row"
                gap={2}
                justifyContent="space-between"
                py={1.5}
                alignItems="center"
              >
                <WahooOnly height="20px" width="auto" />
                {subscriptionData.tag}
              </Stack>
              <Divider />
              <List disablePadding>
                {subscriptionData.items.map(
                  (item: { label: string; value: string | ReactNode }, index: number) => {
                    return (
                      <ListItem
                        key={index}
                        disablePadding
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingY: 1,
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="prose-base"
                          sx={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="prose-base-medium"
                          sx={{
                            wordBreak: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </ListItem>
                    );
                  }
                )}
              </List>
            </Box>
            {subscriptionData?.description}
            {subscriptionData?.action}
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" py={4} gap={1}>
        <Typography variant="ui-lg-bold" px={2}>
          {t`Included with your subscription`}
        </Typography>
        <Box
          sx={{
            width: "100vw",
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
          }}
        >
          <FeatureCarousel />
        </Box>

        <Typography variant="prose-base-bold" pt={2} px={2}>
          {t`Premium access to SYSTM app`}
        </Typography>
        <MenuList
          listItems={[
            {
              id: "systmLink",
              content: (
                <Box display="flex" flexDirection="column">
                  <Typography variant="prose-base">
                    <Trans>SYSTM app</Trans>
                  </Typography>
                  <Typography variant="prose-sm" color="text.secondary">
                    <Trans>Cross-functional training app.</Trans>
                  </Typography>
                </Box>
              ),
              variant: MenuListItemVariant.ExternalLink,
              linkLocation: "systm://home",
              linkComponent: TrackedSystmLink,
              icon: <SystmAppIcon height={44} width={44} style={{ borderRadius: 8 }} />,
            },
          ]}
        />
      </Box>
    </Box>
  );
}

export default SubscriptionPage;
